class BundleMixMatch extends HTMLElement {
  connectedCallback() {
    this.bundleSize       = parseInt(this.dataset.bundleSize, 10) || 8;
    this.parentVariantId  = this.dataset.parentVariantId;
    this.allowDuplicates  = this.dataset.allowDuplicates !== 'false';

    console.group('[BMM] JS init');
    console.log('data-bundle-size attr:', this.dataset.bundleSize, '→ parsed bundleSize:', this.bundleSize);
    console.log('data-allow-duplicates attr:', this.dataset.allowDuplicates, '→ allowDuplicates:', this.allowDuplicates);
    console.log('data-parent-variant-id:', this.parentVariantId);
    console.log('rows found:', this.querySelectorAll('.js-bmm-row').length);
    console.groupEnd();
    this.counts         = {};   // variantId → integer count
    this.variantData    = {};   // variantId → { gid, title, image }
    this._adding        = false;
    this._expandedRow   = null; // currently open row element

    this.rows        = Array.from(this.querySelectorAll('.js-bmm-row'));
    this.counter     = this.querySelector('.js-bmm-counter');
    this.barCounter  = this.querySelector('.js-bmm-bar-counter');
    this.dotsCounter = this.querySelector('.js-bmm-dots-counter');
    this.dots        = Array.from(this.querySelectorAll('.js-bmm-dot'));
    this.cta       = this.querySelector('.js-bmm-cta');
    this.hint      = this.querySelector('.js-bmm-hint');
    this.errorEl   = this.querySelector('.js-bmm-error');

    if (!this.cta || !this.errorEl) return;

    this.rows.forEach((row) => {
      const { variantId, variantGid, variantTitle, variantImage, variantNotes } = row.dataset;
      this.counts[variantId]      = 0;
      this.variantData[variantId] = { gid: variantGid, title: variantTitle, image: variantImage, notes: variantNotes || '' };

      const increment  = row.querySelector('.js-bmm-increment');
      const decrement  = row.querySelector('.js-bmm-decrement');
      const expandBtn  = row.querySelector('.js-bmm-expand-btn');
      const rowMain    = row.querySelector('.bundle-mix-match__row-main');

      // Product link — stop propagation so clicking it doesn't also toggle expand
      const productLink = row.querySelector('.bundle-mix-match__row-product-link');
      if (productLink) productLink.addEventListener('click', (e) => e.stopPropagation());

      // Stepper — stop propagation so clicks don't also toggle expand
      increment.addEventListener('click', (e) => { e.stopPropagation(); this._onIncrement(row); });
      decrement.addEventListener('click', (e) => { e.stopPropagation(); this._onDecrement(row); });

      // Expand toggle — row-main click or expand button click
      if (expandBtn) {
        expandBtn.addEventListener('click', (e) => { e.stopPropagation(); this._toggleExpand(row); });
      }
      if (rowMain) {
        rowMain.addEventListener('click', () => this._toggleExpand(row));
      }
    });

    this.cta.addEventListener('click', () => this._onAddToCart());

    this._initDescriptionExpand();
    this._initLightbox();
    this._hideFloatingWidgets();
  }

  // ─── Lightbox ───────────────────────────────────────────────────────────────

  // ─── Hide floating third-party widgets that overlap the sticky bar ──────────

  _hideFloatingWidgets() {
    const GORGIAS_SELECTOR =
      '#gorgias-chat-container, #gorgias-web-messenger-container, ' +
      '[id^="gorgias"], [class*="gorgias"]';

    const hideMatching = (root) => {
      if (!root) return;
      // Hide the root itself if it matches
      if (root.matches && root.matches(GORGIAS_SELECTOR)) {
        root.style.setProperty('display', 'none', 'important');
      }
      // Hide any matching descendants
      if (root.querySelectorAll) {
        root.querySelectorAll(GORGIAS_SELECTOR).forEach((el) => {
          el.style.setProperty('display', 'none', 'important');
        });
      }
    };

    // Immediate pass — catch anything already in the DOM
    document.querySelectorAll(GORGIAS_SELECTOR).forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });

    // MutationObserver — catch late-injected or re-rendered widgets
    this._gorgiasObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            hideMatching(node);
          }
        }
      }
    });

    this._gorgiasObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  _initLightbox() {
    this._lightbox    = this.querySelector('.js-bmm-lightbox');
    this._lightboxImg = this.querySelector('.js-bmm-lightbox-img');
    if (!this._lightbox) return;

    // Open on image button click
    this.querySelectorAll('.js-bmm-image-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._openLightbox(btn.dataset.lightboxSrc, btn.dataset.lightboxAlt);
      });
    });

    // Close button
    this.querySelector('.js-bmm-lightbox-close')
      ?.addEventListener('click', () => this._lightbox.close());

    // Click anywhere (image or backdrop) closes
    this._lightbox.addEventListener('click', () => this._lightbox.close());
  }

  _openLightbox(src, alt) {
    if (!this._lightbox) return;
    this._lightboxImg.src = src;
    this._lightboxImg.alt = alt || '';
    this._lightbox.showModal();
  }

  // ─── Right panel description expand/collapse ────────────────────────────────

  _initDescriptionExpand() {
    const desc = this.querySelector('.js-bmm-desc');
    if (!desc) return;

    desc.classList.add('bundle-mix-match__panel-description--clamped');

    requestAnimationFrame(() => {
      // If nothing is actually clipped, no button needed — remove the clamp
      if (desc.scrollHeight <= desc.offsetHeight + 2) {
        desc.classList.remove('bundle-mix-match__panel-description--clamped');
        return;
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-mix-match__desc-toggle';
      btn.textContent = 'Read more';

      btn.addEventListener('click', () => {
        desc.classList.toggle('bundle-mix-match__panel-description--clamped');
        btn.textContent = desc.classList.contains('bundle-mix-match__panel-description--clamped')
          ? 'Read more'
          : 'Read less';
      });

      desc.insertAdjacentElement('afterend', btn);
    });
  }

  // ─── Selection ──────────────────────────────────────────────────────────────

  _totalSelected() {
    return Object.values(this.counts).reduce((sum, n) => sum + n, 0);
  }

  _onIncrement(row) {
    if (this._totalSelected() >= this.bundleSize) return;
    const { variantId } = row.dataset;
    if (!this.allowDuplicates && this.counts[variantId] >= 1) return;
    this.counts[variantId]++;
    this._updateUI();
  }

  _onDecrement(row) {
    const { variantId } = row.dataset;
    if (this.counts[variantId] <= 0) return;
    this.counts[variantId]--;
    this._updateUI();
  }

  // ─── Expand / Collapse ──────────────────────────────────────────────────────

  _toggleExpand(row) {
    if (this._expandedRow && this._expandedRow !== row) {
      this._collapseRow(this._expandedRow);
    }
    if (this._expandedRow === row) {
      this._collapseRow(row);
      this._expandedRow = null;
    } else {
      this._expandRow(row);
      this._expandedRow = row;
    }
  }

  _expandRow(row) {
    const panel   = row.querySelector('.js-bmm-expandable');
    const btn     = row.querySelector('.js-bmm-expand-btn');
    const label   = row.querySelector('.js-bmm-expand-label');
    if (!panel) return;

    panel.hidden = false;
    panel.classList.add('bundle-mix-match__expandable--open');
    // Animate: measure natural height then apply it
    panel.style.maxHeight = panel.scrollHeight + 'px';
    row.classList.add('bundle-mix-match__row--expanded');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    if (label) label.textContent = this._collapseLabel();
  }

  _collapseRow(row) {
    const panel = row.querySelector('.js-bmm-expandable');
    const btn   = row.querySelector('.js-bmm-expand-btn');
    const label = row.querySelector('.js-bmm-expand-label');
    if (!panel) return;

    panel.style.maxHeight = '0';
    panel.classList.remove('bundle-mix-match__expandable--open');
    row.classList.remove('bundle-mix-match__row--expanded');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    if (label) label.textContent = this._expandLabel();

    panel.addEventListener('transitionend', () => {
      if (!panel.classList.contains('bundle-mix-match__expandable--open')) {
        panel.hidden = true;
      }
    }, { once: true });
  }

  _expandLabel() {
    // Read from DOM to avoid hardcoding (locale value already rendered in Liquid)
    return this.querySelector('.js-bmm-expand-label')?.textContent || 'View details';
  }

  _collapseLabel() {
    // Fallback — element text is mutated; read from data attribute set on init
    return this._collapseLabelText || 'Hide details';
  }

  // ─── UI Update ──────────────────────────────────────────────────────────────

  _updateUI() {
    const total   = this._totalSelected();
    const atLimit = total >= this.bundleSize;
    const complete = total === this.bundleSize;

    // Panel counter
    if (this.counter) this.counter.textContent = `${total} / ${this.bundleSize}`;

    // Sticky bar counter
    if (this.barCounter) this.barCounter.textContent = `${total} / ${this.bundleSize}`;

    // Dots row counter (mobile top-right)
    if (this.dotsCounter) this.dotsCounter.textContent = `${total} / ${this.bundleSize}`;

    // Hint
    if (this.hint) this.hint.classList.toggle('bundle-mix-match__hint--hidden', complete);

    // Progress dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('bundle-mix-match__dot--filled', i < total);
    });

    // Per-row state
    this.rows.forEach((row) => {
      const { variantId } = row.dataset;
      const count = this.counts[variantId];
      row.querySelector('.js-bmm-row-count').textContent = count;
      row.querySelector('.js-bmm-decrement').disabled = count === 0;
      row.querySelector('.js-bmm-increment').disabled = atLimit || (!this.allowDuplicates && count >= 1);
      row.classList.toggle('bundle-mix-match__row--selected', count > 0);
    });

    // CTA
    this.cta.disabled = !complete;
  }

  _esc(str) {
    return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ─── Cart ───────────────────────────────────────────────────────────────────

  async _onAddToCart() {
    if (this._adding || this._totalSelected() < this.bundleSize) return;
    this._adding = true;
    this._setLoading(true);
    this._clearError();

    // Build ordered GID array and send as a single line item property
    const gids = [];
    this.rows.forEach((row) => {
      const { variantId } = row.dataset;
      for (let i = 0; i < this.counts[variantId]; i++) {
        gids.push(this.variantData[variantId].gid);
      }
    });
    const properties = { _bundle_selections: JSON.stringify(gids) };

    const cartDrawer = document.querySelector('cart-drawer');

    try {
      const body = {
        items: [{ id: parseInt(this.parentVariantId, 10), quantity: 1, properties }],
      };

      if (cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!addRes.ok) {
        let msg = 'Could not add to cart.';
        try { const err = await addRes.json(); msg = err.description || msg; } catch (_) {}
        throw new Error(msg);
      }

      const response = await addRes.json();

      this._adding = false;
      this._setLoading(false);

      if (cartDrawer) {
        cartDrawer.renderContents(response);
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      this._adding = false;
      this._setLoading(false);
      this._showError(err.message || 'Could not add to cart.');
    }
  }

  _setLoading(loading) {
    this.cta.disabled = loading;
    this.cta.classList.toggle('bundle-mix-match__cta--loading', loading);
  }

  _clearError() {
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('bundle-mix-match__error--visible');
  }

  _showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.classList.add('bundle-mix-match__error--visible');
  }
}

customElements.define('bundle-mix-match', BundleMixMatch);
